import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Loader2, Clock, Save, Send, RotateCcw, Sparkles, ArrowLeft, Lightbulb, AlertCircle, CheckCircle2,
} from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Seo from '@/components/shared/Seo';
import { ieltsApi, goToIeltsAuth } from '@/lib/ieltsApi';
import { toast } from 'sonner';
import WinhouseAIFeedbackBadge from '@/components/shared/WinhouseAIFeedbackBadge';

const wordCount = (txt) => (txt || '').trim().split(/\s+/).filter(Boolean).length;
const fmt = (sec) => {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};
const draftKey = (pid) => `ielts_writing_draft_${pid}`;

const BandRow = ({ label, value }) => (
  <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-4 py-3"
    data-testid={`band-row-${label.replace(/\s/g, '-').toLowerCase()}`}>
    <span className="text-sm font-medium text-gray-700">{label}</span>
    <span className="text-2xl font-bold text-black tabular-nums">{value ?? '—'}</span>
  </div>
);

const IeltsWritingPracticePage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const moduleParam = params.get('module') === 'general' ? 'general' : 'academic';
  const taskParam = params.get('task') === '2' ? 2 : 1;
  const promptIdParam = params.get('prompt_id') || '';

  const [mobile, setMobile] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const [essay, setEssay] = useState('');
  const [seconds, setSeconds] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const timerRef = useRef(null);

  const target = taskParam === 1 ? 150 : 250;
  const duration = taskParam === 1 ? 20 * 60 : 40 * 60;
  const remaining = Math.max(0, duration - seconds);
  const overTime = remaining === 0;
  const wc = useMemo(() => wordCount(essay), [essay]);

  // 1) Probe auth and load a prompt
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await ieltsApi.authMe();
        if (!mounted) return;
        setAuthChecked(true);
        loadPrompt();
      } catch (e) {
        if (!mounted) return;
        setAuthChecked(true);
        setNeedsLogin(true);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const loadPrompt = async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const qs = promptIdParam
        ? `?prompt_id=${encodeURIComponent(promptIdParam)}`
        : `?module=${moduleParam}&task_number=${taskParam}`;
      const r = await ieltsApi.writingNextPrompt(qs);
      setPrompt(r.prompt);
      // Restore draft
      try {
        const d = localStorage.getItem(draftKey(r.prompt.prompt_id));
        if (d) {
          const parsed = JSON.parse(d);
          setEssay(parsed.essay || '');
          setSeconds(parsed.seconds || 0);
        } else {
          setEssay('');
          setSeconds(0);
        }
      } catch { setEssay(''); setSeconds(0); }
    } catch (e) {
      toast.error(e.message || 'Could not load a prompt');
    } finally {
      setLoading(false);
    }
  };

  // 2) Timer (only when prompt loaded and not yet submitted)
  useEffect(() => {
    if (!prompt || feedback) return;
    timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [prompt, feedback]);

  // 3) Autosave (debounced) — write to localStorage every 2 sec when essay changes
  useEffect(() => {
    if (!prompt) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(draftKey(prompt.prompt_id), JSON.stringify({ essay, seconds }));
        setSavedAt(new Date());
      } catch { /* quota issues — fine */ }
    }, 800);
    return () => clearTimeout(t);
  }, [essay, seconds, prompt]);

  const handleSubmit = async () => {
    if (!prompt) return;
    if (wc < 30) {
      toast.info('Please write at least 30 words before submitting for AI feedback.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await ieltsApi.writingSubmitEssay({
        prompt_id: prompt.prompt_id,
        essay_text: essay,
        seconds_spent: seconds,
        request_ai_feedback: true,
      });
      setFeedback(r);
      // Clear draft on successful submit
      try { localStorage.removeItem(draftKey(prompt.prompt_id)); } catch { /* ignore */ }
      toast.success('Essay submitted — AI band feedback below');
    } catch (e) {
      toast.error(e.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewPrompt = () => {
    setPrompt(null);
    setEssay('');
    setSeconds(0);
    setFeedback(null);
    loadPrompt();
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-red-700" />
      </div>
    );
  }
  if (needsLogin) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-md text-center bg-white border border-gray-200 rounded-2xl p-8">
            <h1 className="text-2xl font-bold mb-2">Sign in to start writing</h1>
            <p className="text-gray-600 mb-6 text-sm">Your essays and band scores are tracked on your dashboard.</p>
            <Button onClick={() => goToIeltsAuth(`/ielts/writing/practice?module=${moduleParam}&task=${taskParam}`)}
              className="bg-black text-white rounded-full px-6 h-11" data-testid="writing-prac-signin">
              Sign in with Google
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="ielts-writing-practice">
      <Seo title="IELTS Writing Practice · Winhouse" canonical="/ielts/writing/practice" />
      <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />

      <main className="flex-1 max-w-7xl mx-auto px-3 sm:px-6 pt-24 sm:pt-28 pb-8 w-full">
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/ielts/writing')}
              className="rounded-full" data-testid="writing-prac-back">
              <ArrowLeft className="h-4 w-4 mr-1" /> Writing Hub
            </Button>
            {prompt && (
              <>
                <Badge variant="outline" className="capitalize">{prompt.module}</Badge>
                <Badge variant="outline">Task {prompt.task_number}</Badge>
                <Badge variant="outline" className="capitalize">{(prompt.task_type || '').replace(/_/g, ' ')}</Badge>
              </>
            )}
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full font-mono tabular-nums ${
              overTime ? 'bg-red-50 text-red-700 border border-red-200'
              : remaining < 120 ? 'bg-amber-50 text-amber-700 border border-amber-200'
              : 'bg-white text-black border border-gray-200'
            }`} data-testid="writing-prac-timer">
              <Clock className="h-4 w-4" /> {fmt(remaining)}
            </div>
            {savedAt && !feedback && (
              <span className="hidden sm:inline-flex items-center gap-1 text-xs text-gray-500" data-testid="writing-prac-saved">
                <Save className="h-3.5 w-3.5" /> Saved
              </span>
            )}
          </div>
        </div>

        {loading || !prompt ? (
          <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center">
            <Loader2 className="h-7 w-7 mx-auto animate-spin text-red-700" />
            <p className="text-sm text-gray-500 mt-3">Loading prompt…</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-4">
            {/* LEFT: Prompt + instructions */}
            <section className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-7 max-h-[calc(100vh-200px)] overflow-auto"
              data-testid="writing-prac-prompt-pane">
              <div className="text-xs uppercase tracking-[0.18em] text-red-700 font-semibold mb-3">
                Writing Task {prompt.task_number} · {prompt.module}
                {prompt.chart_type ? <span className="ml-2 text-gray-500 normal-case tracking-normal">· {prompt.chart_type}</span> : null}
              </div>
              <h2 className="text-xl sm:text-2xl font-bold leading-snug mb-3" data-testid="writing-prac-task-title">
                {prompt.task_title || (prompt.task_type || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </h2>
              {prompt.instructions && (
                <p className="text-sm text-gray-600 mb-4 italic">{prompt.instructions}</p>
              )}
              {prompt.image_url ? (
                <div className="mb-4 bg-gray-50 border border-gray-200 rounded-xl p-2 sm:p-3"
                  data-testid="writing-prac-prompt-image-wrapper">
                  <img src={prompt.image_url} alt={prompt.task_title || 'Task 1 visual'}
                    className="w-full h-auto max-h-[420px] object-contain mx-auto"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    data-testid="writing-prac-prompt-image" />
                </div>
              ) : null}
              <div className="whitespace-pre-wrap text-[15px] leading-7 text-gray-900 font-serif"
                data-testid="writing-prac-prompt-text">
                {prompt.prompt_text}
              </div>
              <div className="mt-5 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800">
                <Lightbulb className="h-3.5 w-3.5" />
                Minimum {target} words · Recommended {taskParam === 1 ? '20' : '40'} minutes
              </div>
            </section>

            {/* RIGHT: Editor + actions */}
            <section className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-7 flex flex-col"
              data-testid="writing-prac-editor-pane">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="font-bold text-lg">Your essay</h3>
                <div className="flex items-center gap-2 text-xs">
                  <span className={`px-2.5 py-1 rounded-full font-mono tabular-nums ${
                    wc >= target ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-gray-100 text-gray-600 border border-gray-200'
                  }`} data-testid="writing-prac-wc">
                    {wc} / {target} words
                  </span>
                </div>
              </div>
              <textarea
                value={essay}
                onChange={(e) => setEssay(e.target.value)}
                disabled={!!feedback}
                placeholder={taskParam === 1
                  ? 'Begin your response… (Summarise the main features in the first paragraph)'
                  : 'Begin your essay… (Start with a clear thesis statement)'}
                rows={18}
                className="flex-1 w-full resize-y border border-gray-200 rounded-xl p-4 text-[15px] leading-7 font-serif focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none disabled:bg-gray-50"
                data-testid="writing-prac-editor"
              />
              {overTime && !feedback && (
                <div className="mt-3 inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700">
                  <AlertCircle className="h-4 w-4" /> Time is up — submit now for AI band feedback.
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <Button onClick={handleSubmit} disabled={submitting || !!feedback}
                  className="bg-black text-white rounded-full px-6 h-11" data-testid="writing-prac-submit">
                  {submitting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Evaluating…</>
                    : <><Send className="h-4 w-4 mr-1.5" /> Submit for AI Feedback</>}
                </Button>
                <Button variant="outline" onClick={handleNewPrompt} disabled={submitting}
                  className="rounded-full h-11" data-testid="writing-prac-skip">
                  <RotateCcw className="h-4 w-4 mr-1.5" /> Skip / new prompt
                </Button>
              </div>
            </section>
          </div>
        )}

        {/* FEEDBACK */}
        {feedback && (
          <FeedbackPanel feedback={feedback} onNew={handleNewPrompt} onDashboard={() => navigate('/ielts/writing/dashboard')} />
        )}
      </main>
    </div>
  );
};

const FeedbackPanel = ({ feedback, onNew, onDashboard }) => {
  const ai = feedback.ai_feedback || {};
  const bands = ai.bands || {};
  const overall = feedback.estimated_band_overall ?? ai.estimated_band_overall;
  const error = typeof ai.error === 'string' ? ai.error : null;
  return (
    <section className="mt-6 bg-white border border-gray-200 rounded-2xl p-5 sm:p-7" data-testid="writing-prac-feedback">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-red-700" />
          <h3 className="font-bold text-lg">AI Band Feedback</h3>
          <WinhouseAIFeedbackBadge model={ai.model || 'claude-sonnet-4-5'} className="ml-1" />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onDashboard} className="rounded-full" data-testid="writing-prac-feedback-dashboard">
            View dashboard
          </Button>
          <Button onClick={onNew} className="bg-black text-white rounded-full" data-testid="writing-prac-feedback-next">
            Try another prompt
          </Button>
        </div>
      </div>
      {error ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          <AlertCircle className="h-4 w-4 inline mr-1.5" />
          {error}
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            <div className="bg-black text-white rounded-2xl p-5 sm:col-span-1 lg:col-span-1 flex flex-col justify-center" data-testid="band-row-overall">
              <span className="text-xs uppercase tracking-wider text-white/70 mb-1">Overall</span>
              <span className="text-4xl font-bold text-[#D3D86F]">{overall ?? '—'}</span>
              <span className="text-xs text-white/60 mt-1">Estimated band</span>
            </div>
            <BandRow label="Task Achievement" value={bands.task_achievement} />
            <BandRow label="Coherence & Cohesion" value={bands.coherence_cohesion} />
            <BandRow label="Lexical Resource" value={bands.lexical_resource} />
            <BandRow label="Grammar Range & Accuracy" value={bands.grammatical_range_accuracy} />
          </div>

          {ai.summary && (
            <div className="mb-6 bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm leading-7" data-testid="ai-summary">
              <strong className="font-semibold">Examiner summary: </strong>{ai.summary}
            </div>
          )}

          {ai.feedback && (
            <div className="grid md:grid-cols-2 gap-3 mb-6">
              {[
                ['task_achievement', 'Task Achievement'],
                ['coherence_cohesion', 'Coherence & Cohesion'],
                ['lexical_resource', 'Lexical Resource'],
                ['grammatical_range_accuracy', 'Grammar'],
              ].map(([k, label]) => ai.feedback[k] ? (
                <div key={k} className="border border-gray-200 rounded-xl p-4 text-sm leading-7 bg-white">
                  <div className="text-xs uppercase tracking-wider text-red-700 font-semibold mb-1">{label}</div>
                  {ai.feedback[k]}
                </div>
              ) : null)}
            </div>
          )}

          {Array.isArray(ai.sentence_edits) && ai.sentence_edits.length > 0 && (
            <div className="mb-6" data-testid="ai-sentence-edits">
              <h4 className="font-semibold mb-2 text-sm uppercase tracking-wider text-gray-500">Sentence-level edits</h4>
              <div className="space-y-2">
                {ai.sentence_edits.map((e, i) => (
                  <div key={i} className="border border-gray-200 rounded-xl p-3 text-sm">
                    <div className="text-red-700 line-through opacity-80">{e.original}</div>
                    <div className="text-green-700 mt-1"><CheckCircle2 className="h-3.5 w-3.5 inline mr-1" />{e.improved}</div>
                    {e.reason && <div className="text-gray-500 text-xs mt-1 italic">{e.reason}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(ai.vocabulary_upgrades) && ai.vocabulary_upgrades.length > 0 && (
            <div className="mb-6" data-testid="ai-vocab-upgrades">
              <h4 className="font-semibold mb-2 text-sm uppercase tracking-wider text-gray-500">Vocabulary upgrades</h4>
              <div className="flex flex-wrap gap-2">
                {ai.vocabulary_upgrades.map((v, i) => (
                  <span key={i} className="px-3 py-1 bg-[#D3D86F]/20 border border-[#D3D86F] rounded-full text-xs font-medium">{v}</span>
                ))}
              </div>
            </div>
          )}

          {ai.sample_high_band_rewrite && (
            <div className="bg-black text-white rounded-2xl p-5" data-testid="ai-rewrite">
              <div className="text-xs uppercase tracking-wider text-[#D3D86F] font-semibold mb-2">Band 8 rewrite (sample paragraph)</div>
              <div className="text-sm leading-7 font-serif text-white/90 whitespace-pre-wrap">{ai.sample_high_band_rewrite}</div>
            </div>
          )}
        </>
      )}
      <WinhouseAIFeedbackBadge variant="footer" model={ai.model || 'claude-sonnet-4-5'} />
    </section>
  );
};

export default IeltsWritingPracticePage;
